/**
 * Chart Colors Utility
 * 
 * Provides neutral grayscale colors for charts.
 * Removed colorful accents per the black/white design requirement.
 */

// Grayscale chart colors
export const chartColors = [
  'rgb(23, 23, 23)',     // Almost black
  'rgb(64, 64, 64)',     // Dark gray
  'rgb(115, 115, 115)',  // Medium gray
  'rgb(163, 163, 163)',  // Light gray
  'rgb(212, 212, 212)',  // Very light gray
]

// Dark mode chart colors (inverted)
export const chartColorsDark = [
  'rgb(245, 245, 245)',  // Almost white
  'rgb(212, 212, 212)',  // Very light gray
  'rgb(163, 163, 163)',  // Light gray
  'rgb(115, 115, 115)',  // Medium gray
  'rgb(64, 64, 64)',     // Dark gray
]

/**
 * Get chart colors based on current theme
 */
export function getChartColors() {
  const isDark = document.documentElement.classList.contains('dark')
  return isDark ? chartColorsDark : chartColors
}

/**
 * Get CSS variable value as RGB string
 * @param {string} varName - CSS variable name (e.g., '--foreground')
 * @returns {string} RGB color string
 */
export function getCSSVariableAsRGB(varName) {
  const isDark = document.documentElement.classList.contains('dark')

  // Fallback values based on our neutral theme
  const fallbacks = {
    '--foreground': isDark ? 'rgb(245, 245, 245)' : 'rgb(23, 23, 23)',
    '--muted-foreground': isDark ? 'rgb(163, 163, 163)' : 'rgb(115, 115, 115)',
    '--border': isDark ? 'rgb(64, 64, 64)' : 'rgb(229, 229, 229)',
    '--primary': isDark ? 'rgb(245, 245, 245)' : 'rgb(23, 23, 23)',
    '--popover': isDark ? 'rgb(38, 38, 38)' : 'rgb(255, 255, 255)',
    '--popover-foreground': isDark ? 'rgb(245, 245, 245)' : 'rgb(23, 23, 23)',
  }

  return fallbacks[varName] || (isDark ? 'rgb(245, 245, 245)' : 'rgb(23, 23, 23)')
}

/**
 * Get CSS variable value as RGBA string
 * @param {string} varName - CSS variable name
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string
 */
export function getCSSVariableAsRGBA(varName, alpha = 1) {
  const rgb = getCSSVariableAsRGB(varName)
  return rgb.replace('rgb', 'rgba').replace(')', `, ${alpha})`)
}

/**
 * Get contrast text color for a background
 * @param {string} bgColor - Background color
 * @returns {string} Text color for contrast
 */
export function getContrastTextColor(bgColor) {
  // For our neutral theme, this is simplified
  return document.documentElement.classList.contains('dark')
    ? 'rgb(23, 23, 23)'
    : 'rgb(245, 245, 245)'
}

export default {
  chartColors,
  chartColorsDark,
  getChartColors,
  getCSSVariableAsRGB,
  getCSSVariableAsRGBA,
  getContrastTextColor,
}
